import * as React from "react";

import { locationRequirement } from "../../DeliveryLocation/Search/lib";
import { ProductInventoryProps } from "./types";
import * as usersettings from "../../DeliveryLocation/user-settings";
import { LoadingProduct } from "./LoadingProduct";
import { OpenLocationSearchButton } from "./OpenLocationSearchButton";
import { UnableToAddToCart } from "./UnableToAddToCart";
import { LocationSearchModal } from "../../DeliveryLocation/Search/LocationSearchModal";

import "./locationSearchModal.scss";
import { useInventoryContext } from "../context";
import { safeRedirectPage, safeReloadPage } from "src/lib/common";
import { SimpleDialog } from "src/components/Common/SimpleDialog";

/**
 * Verifies inventory levels for a product. Conditionally displays UI based on inventory results.
 *
 * @param param0
 * @returns
 */
export const ProductInventory: React.FunctionComponent<
  ProductInventoryProps
> = ({ ProductId, Sku, onInventoryChecked }) => {
  const [inventory, setInventory] = React.useState<InventoryResult[]>([]);
  const [needsAddress, setNeedsAddress] = React.useState(false);
  const [canAddToCart, setCanAddToCart] = React.useState(false);
  const [showLocationSelection, setShowLocationSelection] =
    React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);

  const inventoryContext = useInventoryContext();

  // inventory address requirements
  const requirement = locationRequirement(inventory);
  const visibleOnLoad = showLocationSelection; // false;

  React.useEffect(() => {
    async function getInventoryInner() {
      try {
        const prodId = ProductId;

        const newInventory = await inventoryContext.getInventory({
          PostalCode: deliveryAddress.PostalCode,
          LocationId: deliveryAddress.LocationId,
          ProductId: prodId,
          Sku: Sku,
        });

        setInventory(newInventory);
        if (newInventory.length > 0) {
          onInventoryChecked(newInventory[0]);
          if (newInventory[0].StoreNumber) {
            setCanAddToCart(true);
          }
        }

        setIsLoading(false);
      } catch (_e) {
        setInventory([]);
        setCanAddToCart(false);
        setIsLoading(false);
      }
    }

    const deliveryAddress = usersettings.getDeliveryAddressInfo();

    if (deliveryAddress.LocationId === 0 && !deliveryAddress.PostalCode) {
      setNeedsAddress(true);
    }

    getInventoryInner();
  }, [onInventoryChecked, ProductId, Sku, inventoryContext]);

  React.useEffect(() => {
    if (showLocationSelection) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      // just in case this component unmounts while scroll is locked
      document.body.style.overflow = "";
    };
  }, [showLocationSelection]);

  if (isLoading) {
    return <LoadingProduct />;
  }

  const openLocationSelection = () => {
    setShowLocationSelection(true);
  };

  const handleSelection = () => {
    const currentPath = window.location.pathname.toLowerCase();
    const fileName = currentPath.substring(currentPath.lastIndexOf("/") + 1);
    if (fileName.startsWith("detail.aspx")) {
      safeReloadPage();
    } else {
      const detailUrl = "detail.aspx?id=" + ProductId.toString();
      safeRedirectPage(detailUrl);
    }
  };

  return (
    <div>
      {!canAddToCart && needsAddress && (
        <OpenLocationSearchButton onLocationSelection={openLocationSelection} />
      )}

      {!canAddToCart && !needsAddress && <UnableToAddToCart />}

      <SimpleDialog
        title="Select Delivery Address"
        onClose={() => setShowLocationSelection(false)}
        open={visibleOnLoad}
      >
        <LocationSearchModal
          addressRequirement={requirement}
          onLocationSelected={handleSelection}
          onZipSelected={handleSelection}
        />
      </SimpleDialog>
    </div>
  );
};
